import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  TextInput,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { useRoute, useNavigation, useFocusEffect, RouteProp } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import Svg, { Circle as SvgCircle } from 'react-native-svg';
import { Ionicons } from '@expo/vector-icons';
import { useLanguage } from '../../contexts/LanguageContext';
import { useResponsive } from '../../hooks/useResponsive';
import { PageContainer, Navbar, Breadcrumb, Toast } from '../../components';
import { FONTS } from '../../constants/config';
import { createShadow } from '../../utils/styles';
import api from '../../services/api';
import { MainStackParamList } from '../../navigation/MainNavigator';

type ScreenRouteProp = RouteProp<MainStackParamList, 'AddTalentKPIReading'>;
type NavigationProp = NativeStackNavigationProp<MainStackParamList>;

const FOREGROUND = '#414042';

// ── Helper labels ──────────────────────────────────────────
const polarityLabel = (val: string | undefined, isRTL: boolean) => {
  if (!val) return '-';
  if (val === 'positive' || val === 'ascending' || val === 'increasing') return isRTL ? 'تصاعدي' : 'Ascending';
  if (val === 'negative' || val === 'descending' || val === 'decreasing') return isRTL ? 'تنازلي' : 'Descending';
  return val;
};

const kpiTypeLabel = (val: string | undefined, isRTL: boolean) => {
  if (!val) return '-';
  if (val === 'operational') return isRTL ? 'تشغيلي' : 'Operational';
  if (val === 'strategic') return isRTL ? 'استراتيجي' : 'Strategic';
  return val;
};

const unitLabel = (val: string | undefined, isRTL: boolean) => {
  if (!val) return '-';
  const map: Record<string, string> = {
    '%': isRTL ? 'نسبة' : 'Percentage',
    percentage: isRTL ? 'نسبة' : 'Percentage',
    'عدد': isRTL ? 'عدد' : 'Count',
    number: isRTL ? 'عدد' : 'Number',
    count: isRTL ? 'عدد' : 'Count',
  };
  return map[val] || val;
};

// ── Mock data ──────────────────────────────────────────────
const MOCK_SCHEDULED_PERIODS = [
  { id: 'aug_2026', label_ar: 'أغسطس، 2026', label_en: 'August, 2026', overdue: false },
];
const MOCK_OVERDUE_PERIODS = [
  { id: 'jun_2026', label_ar: 'يونيو، 2026', label_en: 'June, 2026', overdue: true },
  { id: 'jul_2026', label_ar: 'يوليو، 2026', label_en: 'July, 2026', overdue: true },
];
const MOCK_RECENT_READINGS = [
  { value: 527, unit_ar: 'عدد', unit_en: 'Count', date_ar: '30 سبتمبر، 2025', date_en: 'Sep 30, 2025' },
  { value: 480, unit_ar: 'عدد', unit_en: 'Count', date_ar: '30 يونيو، 2025', date_en: 'Jun 30, 2025' },
  { value: 460, unit_ar: 'عدد', unit_en: 'Count', date_ar: '31 مارس، 2025', date_en: 'Mar 31, 2025' },
];
const MOCK_ACHIEVEMENT = { readingsDone: 3, readingsTotal: 4, complianceRate: 92 };

// ── Donut ring component ────────────────────────────────────
function DonutRing({ pct, size = 56, stroke = 6 }: { pct: number; size?: number; stroke?: number }) {
  const radius = (size - stroke) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (Math.min(pct, 100) / 100) * circumference;
  return (
    <Svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <SvgCircle cx={size / 2} cy={size / 2} r={radius} stroke="#E5E7EB" strokeWidth={stroke} fill="none" />
      <SvgCircle
        cx={size / 2} cy={size / 2} r={radius}
        stroke="#22C55E" strokeWidth={stroke} fill="none"
        strokeDasharray={`${circumference}`} strokeDashoffset={offset}
        strokeLinecap="round" rotation={-90} origin={`${size / 2}, ${size / 2}`}
      />
    </Svg>
  );
}

// ── Radio button component ──────────────────────────────────
function RadioButton({ selected, onPress, disabled }: { selected: boolean; onPress: () => void; disabled?: boolean }) {
  return (
    <TouchableOpacity
      onPress={onPress}
      disabled={disabled}
      style={[styles.radio, selected && styles.radioSelected]}
      activeOpacity={0.7}
    >
      {selected && <View style={styles.radioDot} />}
    </TouchableOpacity>
  );
}

// ════════════════════════════════════════════════════════════
// Main Component
// ════════════════════════════════════════════════════════════
export default function AddTalentKPIReadingScreen() {
  const route = useRoute<ScreenRouteProp>();
  const navigation = useNavigation<NavigationProp>();
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const { isMobile } = useResponsive();
  const containerPadding = isMobile ? 12 : 24;

  const kpiLinkId = route.params?.kpiLinkId;

  // ── Data state ──────────────────────────────────────────
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [linkData, setLinkData] = useState<any>(null);

  // ── Form state ──────────────────────────────────────────
  const [selectedPeriod, setSelectedPeriod] = useState(MOCK_SCHEDULED_PERIODS[0]?.id || '');
  const [achievedValue, setAchievedValue] = useState('');
  const [evidenceMode, setEvidenceMode] = useState<'file' | 'text'>('file');
  const [evidenceFile, setEvidenceFile] = useState<File | null>(null);
  const [evidenceText, setEvidenceText] = useState('');
  const [notes, setNotes] = useState('');
  // Per-student selection for general (overall) KPIs
  const [students, setStudents] = useState<{ id: number; name: string }[]>([]);
  const [selectedStudentId, setSelectedStudentId] = useState<number | null>(null);

  // ── Toast ───────────────────────────────────────────────
  const [toastVisible, setToastVisible] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error'>('success');

  const showToast = (msg: string, type: 'success' | 'error') => {
    setToastMessage(msg);
    setToastType(type);
    setToastVisible(true);
  };

  // ── Fetch KPI link data ─────────────────────────────────
  useFocusEffect(
    useCallback(() => {
      const fetchData = async () => {
        if (!kpiLinkId) return;
        setIsLoading(true);
        try {
          const resp = await api.get(`/talent-kpi-links/${kpiLinkId}/`);
          setLinkData(resp.data);
        } catch (e) {
          console.log('[AddTalentKPIReading] Fetch failed:', e);
        }
        setIsLoading(false);
      };
      fetchData();
    }, [kpiLinkId])
  );

  // For a general (overall) KPI, load the students enrolled in whatever the KPI
  // hangs off — talent, project or activity — so each reading can be attributed
  // to one student (achievement is averaged across them).
  const isGeneralKPI = !!linkData?.kpi_is_general;
  const entityModel = linkData?.entity_model;
  useEffect(() => {
    if (!isGeneralKPI || !linkData?.object_id) return;
    (async () => {
      try {
        const scopeParam =
          entityModel === 'talentproject' ? { project: linkData.object_id }
          : entityModel === 'talentactivity' ? { activity: linkData.object_id }
          : { specialization_fk: linkData.object_id };
        const resp = await api.get('/talent-students/', { params: scopeParam });
        const rows = resp.data?.results || resp.data || [];
        setStudents(rows.map((s: any) => ({ id: s.id, name: s.name })));
      } catch (e) {
        setStudents([]);
      }
    })();
  }, [isGeneralKPI, linkData?.object_id, entityModel]);

  // ── Derived values ──────────────────────────────────────
  const kpiName = linkData?.kpi_name || '';
  const kpiType = linkData?.kpi_type || '';
  const targetValue = parseFloat(linkData?.kpi_target_value || linkData?.target_value) || 0;
  const kpiPolarity = linkData?.kpi_polarity || '';
  const kpiOwner = linkData?.kpi_owner || '';
  const kpiUnit = linkData?.kpi_unit || '';
  const kpiUnitDisplay = unitLabel(kpiUnit, isRTL) || (isRTL ? 'طالب' : 'Student');

  // ── File picker ─────────────────────────────────────────
  const handleFilePick = () => {
    if (Platform.OS === 'web') {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx';
      input.onchange = (e: any) => {
        const file = e.target?.files?.[0];
        if (file) setEvidenceFile(file);
      };
      input.click();
    }
  };

  // ── Save handler ────────────────────────────────────────
  const handleSave = async () => {
    if (!achievedValue.trim()) {
      showToast(isRTL ? 'يرجى إدخال القيمة المحققة' : 'Please enter achieved value', 'error');
      return;
    }
    if (isGeneralKPI && !selectedStudentId) {
      showToast(isRTL ? 'يرجى اختيار الطالب' : 'Please select a student', 'error');
      return;
    }
    // Evidence is optional — warn but don't block
    if ((evidenceMode === 'file' && !evidenceFile) || (evidenceMode === 'text' && !evidenceText.trim())) {
      // No evidence provided — still allow save
    }

    setIsSaving(true);
    try {
      const formData = new FormData();
      formData.append('value', achievedValue);
      formData.append('period', selectedPeriod);
      // Measurement date drives which grid-period target the reading scores
      // against; default to today.
      formData.append('measurement_date', new Date().toISOString().slice(0, 10));
      if (isGeneralKPI && selectedStudentId) {
        formData.append('student_id', String(selectedStudentId));
      }
      if (evidenceMode === 'file' && evidenceFile) {
        formData.append('evidence_file', evidenceFile as any);
      } else if (evidenceMode === 'text') {
        formData.append('evidence_text', evidenceText);
      }
      if (notes.trim()) {
        formData.append('notes', notes);
      }

      await api.post(`/talent-kpi-links/${kpiLinkId}/add-reading/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      showToast(isRTL ? 'تم حفظ القراءة بنجاح' : 'Reading saved successfully', 'success');
      setTimeout(() => navigation.goBack(), 600);
    } catch (err: any) {
      console.error('[AddTalentKPIReading] Save failed:', err);
      const msg = err?.response?.data?.detail || err?.message || (isRTL ? 'فشل في حفظ القراءة' : 'Failed to save reading');
      showToast(msg, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  // ── Info card row helper ────────────────────────────────
  const InfoRow = ({ label, value }: { label: string; value: string }) => (
    <View style={[styles.infoRow, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
      <View style={styles.infoLabelPill}>
        <Text style={[styles.infoLabelText, { textAlign: isRTL ? 'right' : 'left' }]}>{label}</Text>
      </View>
      <Text style={[styles.infoValueText, { textAlign: isRTL ? 'right' : 'left' }]}>{value}</Text>
    </View>
  );

  // ── Tab state ────────────────────────────────────────
  const [activeTab, setActiveTab] = useState<'manual' | 'import'>('manual');

  // ── Import file state ──────────────────────────────────
  const [importFile, setImportFile] = useState<File | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [updateExisting, setUpdateExisting] = useState(true);

  const handleImportFilePick = () => {
    if (Platform.OS === 'web') {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.csv,.xls,.xlsx';
      input.onchange = (e: any) => {
        const file = e.target?.files?.[0];
        if (file) setImportFile(file);
      };
      input.click();
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      showToast(isRTL ? 'يرجى اختيار ملف' : 'Please select a file', 'error');
      return;
    }
    setIsImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', importFile as any);
      await api.post(`/talent-kpi-links/${kpiLinkId}/import-readings/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      showToast(isRTL ? 'تم استيراد القراءات بنجاح' : 'Readings imported successfully', 'success');
      setTimeout(() => navigation.goBack(), 600);
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || (isRTL ? 'فشل في استيراد القراءات' : 'Failed to import readings');
      showToast(msg, 'error');
    } finally {
      setIsImporting(false);
    }
  };

  // ════════════════════════════════════════════════════════
  // Render
  // ════════════════════════════════════════════════════════
  return (
    <PageContainer>
      <Navbar />
      <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollContent}>
        <View style={[styles.container, { padding: containerPadding }]}>
          <Breadcrumb
            items={[
              { label: isRTL ? 'الرئيسية' : 'Home' },
              { label: isRTL ? 'مبادرة مواهب' : 'Talents Initiative', route: 'Talents' },
              { label: isRTL ? 'تفاصيل مؤشر الأداء' : 'KPI Details' },
              { label: isRTL ? 'إضافة قراءة' : 'Add Reading' },
            ]}
          />

          {isLoading ? (
            <View style={styles.loadingWrap}>
              <ActivityIndicator size="large" color={FOREGROUND} />
            </View>
          ) : (
            <View style={styles.sections}>
              {/* ── Title Card (full width) ─────────────────── */}
              <View style={[styles.card, createShadow(0, 1, 3, '#000', 0.1)]}>
                <Text style={[styles.kpiNameTitle, { textAlign: isRTL ? 'right' : 'left' }]}>
                  {kpiName}
                </Text>
              </View>

              {/* ── Tab Switcher ────────────────────────────── */}
              <View style={[styles.tabBar, createShadow(0, 1, 3, '#000', 0.1)]}>
                <View style={[styles.tabRow, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
                  <TouchableOpacity
                    style={[styles.tabBtn, activeTab === 'manual' && styles.tabBtnActive]}
                    onPress={() => setActiveTab('manual')}
                    activeOpacity={0.7}
                  >
                    <Text style={[styles.tabBtnText, activeTab === 'manual' && styles.tabBtnTextActive]}>
                      {isRTL ? 'إدخال قيمة القراءات يدوياً' : 'Enter readings manually'}
                    </Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[styles.tabBtn, activeTab === 'import' && styles.tabBtnActive]}
                    onPress={() => setActiveTab('import')}
                    activeOpacity={0.7}
                  >
                    <Text style={[styles.tabBtnText, activeTab === 'import' && styles.tabBtnTextActive]}>
                      {isRTL ? 'استيراد قيم القراءات' : 'Import reading values'}
                    </Text>
                  </TouchableOpacity>
                </View>
              </View>

              {/* ── 3-column grid ───────────────────────────── */}
              <View style={[
                styles.gridRow,
                isMobile && styles.gridRowMobile,
              ]}>
                {/* ── RIGHT SIDE (1/3): Info sidebar ─────── */}
                <View style={[styles.sidebarCol, isMobile && styles.fullWidth]}>
                  {/* Card 1: KPI Info */}
                  <View style={[styles.card, createShadow(0, 1, 3, '#000', 0.1)]}>
                    <Text style={[styles.sectionTitle, { textAlign: isRTL ? 'right' : 'left' }]}>
                      {isRTL ? 'معلومات المؤشر' : 'KPI Info'}
                    </Text>
                    <InfoRow label={isRTL ? 'النوع' : 'Type'} value={kpiTypeLabel(kpiType, isRTL)} />
                    <InfoRow label={isRTL ? 'المستهدف' : 'Target'} value={`${targetValue} ${kpiUnitDisplay}`} />
                    <InfoRow label={isRTL ? 'القطبية' : 'Polarity'} value={polarityLabel(kpiPolarity, isRTL)} />
                    <InfoRow label={isRTL ? 'المسؤول' : 'Owner'} value={kpiOwner || '-'} />
                  </View>

                  {/* Card 2: Current Year Achievement */}
                  <View style={[styles.card, createShadow(0, 1, 3, '#000', 0.1)]}>
                    <Text style={[styles.sectionTitle, { textAlign: isRTL ? 'right' : 'left' }]}>
                      {isRTL ? 'إنجاز السنة الحالية' : 'Current Year Achievement'}
                    </Text>
                    <View style={[styles.achievementRow, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
                      <DonutRing pct={MOCK_ACHIEVEMENT.complianceRate} />
                      <View style={styles.achievementMetrics}>
                        <View style={[styles.achievementMetricRow, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
                          <Text style={[styles.achievementMetricLabel, { textAlign: isRTL ? 'right' : 'left' }]}>
                            {isRTL ? 'القراءات' : 'Readings'}
                          </Text>
                          <Text style={styles.achievementMetricValue}>
                            {MOCK_ACHIEVEMENT.readingsDone}/{MOCK_ACHIEVEMENT.readingsTotal}
                          </Text>
                        </View>
                        <View style={[styles.achievementMetricRow, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
                          <Text style={[styles.achievementMetricLabel, { textAlign: isRTL ? 'right' : 'left' }]}>
                            {isRTL ? 'معدل الالتزام' : 'Compliance'}
                          </Text>
                          <Text style={styles.achievementMetricValue}>
                            {MOCK_ACHIEVEMENT.complianceRate} %
                          </Text>
                        </View>
                      </View>
                    </View>
                  </View>

                  {/* Card 3: Recent Readings */}
                  <View style={[styles.card, createShadow(0, 1, 3, '#000', 0.1)]}>
                    <Text style={[styles.sectionTitle, { textAlign: isRTL ? 'right' : 'left' }]}>
                      {isRTL ? 'آخر القراءات' : 'Recent Readings'}
                    </Text>
                    {MOCK_RECENT_READINGS.map((r, idx) => (
                      <View key={idx} style={styles.recentReadingRow}>
                        <Text style={[styles.recentReadingValue, { textAlign: isRTL ? 'right' : 'left' }]}>
                          {r.value} {isRTL ? r.unit_ar : r.unit_en}
                        </Text>
                        <Text style={[styles.recentReadingDate, { textAlign: isRTL ? 'right' : 'left' }]}>
                          {isRTL ? r.date_ar : r.date_en}
                        </Text>
                      </View>
                    ))}
                  </View>
                </View>

                {/* ── LEFT SIDE (2/3): Main form card ──────── */}
                <View style={[styles.formCard, styles.card, createShadow(0, 1, 3, '#000', 0.1), isMobile && styles.fullWidth]}>
                  {activeTab === 'import' ? (
                    /* ── IMPORT TAB ──────────────────────────── */
                    <View>
                      {/* 1. Period Selection (same as manual) */}
                      <View style={styles.periodSection}>
                        <Text style={[styles.periodGroupLabel, { textAlign: isRTL ? 'right' : 'left' }]}>
                          {isRTL ? 'فترات مجدولة' : 'Scheduled Periods'}
                        </Text>
                        {MOCK_SCHEDULED_PERIODS.map((p) => (
                          <View key={p.id} style={[styles.periodRow, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
                            <RadioButton selected={selectedPeriod === p.id} onPress={() => setSelectedPeriod(p.id)} />
                            <Text style={[styles.periodText, { textAlign: isRTL ? 'right' : 'left' }]}>
                              {isRTL ? p.label_ar : p.label_en}
                            </Text>
                          </View>
                        ))}
                      </View>

                      {/* 2. Import from Excel section */}
                      <View style={styles.importSection}>
                        <Text style={[styles.importSectionTitle, { textAlign: isRTL ? 'right' : 'left' }]}>
                          {isRTL ? 'استيراد قيم القراءات من Excel' : 'Import reading values from Excel'}
                        </Text>

                        {/* File picker field */}
                        <Text style={[styles.fieldLabel, { textAlign: isRTL ? 'right' : 'left', marginTop: 16 }]}>
                          <Text style={styles.requiredStar}>* </Text>
                          {isRTL ? 'اختر ملف Excel' : 'Choose Excel file'}
                        </Text>
                        <View style={[styles.importFileInput, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
                          <TouchableOpacity style={styles.importChooseBtn} onPress={handleImportFilePick}>
                            <Text style={styles.importChooseBtnText}>
                              {isRTL ? 'اختر ملف' : 'Choose file'}
                            </Text>
                          </TouchableOpacity>
                          <View style={[styles.importFileNameBox, { flex: 1 }]}>
                            <Text style={[styles.importFileNameText, { textAlign: isRTL ? 'right' : 'left' }]} numberOfLines={1}>
                              {importFile ? importFile.name : (isRTL ? 'لم يتم اختيار ملف' : 'No file selected')}
                            </Text>
                          </View>
                        </View>

                        {/* File format hint */}
                        <View style={[styles.infoBar, { flexDirection: isRTL ? 'row-reverse' : 'row', marginTop: 8 }]}>
                          <Ionicons name="information-circle-outline" size={16} color="#64748B" />
                          <Text style={[styles.infoBarText, { textAlign: isRTL ? 'right' : 'left' }]}>
                            {isRTL
                              ? 'الملف يجب أن يكون بصيغة Excel (.xlsx أو .xls) ومطابق للنموذج المعتمد'
                              : 'File must be Excel format (.xlsx or .xls) matching the approved template'}
                          </Text>
                        </View>

                        {/* Update existing checkbox */}
                        <TouchableOpacity
                          style={[styles.checkboxRow, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}
                          onPress={() => setUpdateExisting(!updateExisting)}
                          activeOpacity={0.7}
                        >
                          <View style={[styles.checkbox, updateExisting && styles.checkboxChecked]}>
                            {updateExisting && <Ionicons name="checkmark" size={14} color="#FFFFFF" />}
                          </View>
                          <Text style={[styles.checkboxLabel, { textAlign: isRTL ? 'right' : 'left' }]}>
                            {isRTL ? 'تحديث البيانات الجديدة إن وجدت' : 'Update existing data if found'}
                          </Text>
                        </TouchableOpacity>
                        <View style={[styles.infoBar, { flexDirection: isRTL ? 'row-reverse' : 'row', marginTop: 4 }]}>
                          <Ionicons name="information-circle-outline" size={16} color="#64748B" />
                          <Text style={[styles.infoBarText, { textAlign: isRTL ? 'right' : 'left' }]}>
                            {isRTL
                              ? 'إذا لم تحدد هذا الخيار، سيتم تجاهل البيانات الموجودة وإضافة الجديدة فقط'
                              : 'If unchecked, existing data will be ignored and only new data will be added'}
                          </Text>
                        </View>
                      </View>

                      {/* 3. Important instructions */}
                      <View style={styles.importInstructions}>
                        <Text style={[styles.importInstructionsTitle, { textAlign: isRTL ? 'right' : 'left' }]}>
                          {isRTL ? 'تعليمات هامة' : 'Important Instructions'}
                        </Text>
                        {(isRTL ? [
                          'يجب أن يحتوي الملف على المعلومات التالية: اسم الطالب كما هو في النظام، قيمة القراءة، رابط الدليل المرفق أو النص البديل، الملاحظات في حال كانت القراءة متأخرة.',
                          'يُفضل استخدام ملف تم تصديره من النظام لضمان التوافق',
                          'البيانات الموجودة سيتم تحديثها بناءً على معرف ID أو الاسم حصراً للقراءات الغير مدخلة سابقاً',
                          'يتم الاحتفاظ بنسخة احتياطية تلقائياً قبل الاستيراد',
                        ] : [
                          'File must contain: student name (as in system), reading value, evidence link or text, notes if reading is overdue.',
                          'It is recommended to use a file exported from the system to ensure compatibility',
                          'Existing data will be updated based on ID or name only for readings not previously entered',
                          'A backup is automatically saved before import',
                        ]).map((instruction, idx) => (
                          <View key={idx} style={[styles.instructionRow, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
                            <Text style={styles.instructionBullet}>•</Text>
                            <Text style={[styles.instructionText, { textAlign: isRTL ? 'right' : 'left' }]}>
                              {instruction}
                            </Text>
                          </View>
                        ))}
                      </View>

                      {/* 4. Footer buttons */}
                      <View style={[styles.footerButtons, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
                        <TouchableOpacity
                          style={[styles.saveBtn, (isImporting || !importFile) && styles.saveBtnDisabled]}
                          onPress={handleImport}
                          disabled={isImporting || !importFile}
                        >
                          {isImporting ? (
                            <ActivityIndicator size="small" color="#FFFFFF" />
                          ) : (
                            <Text style={styles.saveBtnText}>{isRTL ? 'استيراد البيانات' : 'Import Data'}</Text>
                          )}
                        </TouchableOpacity>
                        <TouchableOpacity
                          style={styles.cancelBtn}
                          onPress={() => navigation.goBack()}
                          disabled={isImporting}
                        >
                          <Text style={styles.cancelBtnText}>{isRTL ? 'إلغاء' : 'Cancel'}</Text>
                        </TouchableOpacity>
                      </View>
                    </View>
                  ) : (
                    /* ── MANUAL ENTRY TAB ────────────────────── */
                    <View>
                  <Text style={[styles.sectionTitle, { textAlign: isRTL ? 'right' : 'left' }]}>
                    {isRTL ? 'إدخال قيمة القراءة' : 'Enter Reading Value'}
                  </Text>

                  {/* 1. Period Selection ────────────────────── */}
                  <View style={styles.periodSection}>
                    {/* Scheduled periods */}
                    <Text style={[styles.periodGroupLabel, { textAlign: isRTL ? 'right' : 'left' }]}>
                      {isRTL ? 'فترات مجدولة' : 'Scheduled Periods'}
                    </Text>
                    {MOCK_SCHEDULED_PERIODS.map((p) => (
                      <View key={p.id} style={[styles.periodRow, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
                        <RadioButton selected={selectedPeriod === p.id} onPress={() => setSelectedPeriod(p.id)} />
                        <Text style={[styles.periodText, { textAlign: isRTL ? 'right' : 'left' }]}>
                          {isRTL ? p.label_ar : p.label_en}
                        </Text>
                      </View>
                    ))}

                    {/* Overdue periods */}
                    {MOCK_OVERDUE_PERIODS.length > 0 && (
                      <>
                        <Text style={[styles.periodGroupLabel, styles.periodGroupLabelOverdue, { textAlign: isRTL ? 'right' : 'left' }]}>
                          {isRTL ? 'فترات متأخرة' : 'Overdue Periods'}
                        </Text>
                        {MOCK_OVERDUE_PERIODS.map((p) => (
                          <View key={p.id} style={[styles.periodRow, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
                            <RadioButton selected={selectedPeriod === p.id} onPress={() => setSelectedPeriod(p.id)} />
                            <Text style={[styles.periodText, styles.periodTextOverdue, { textAlign: isRTL ? 'right' : 'left' }]}>
                              {isRTL ? p.label_ar : p.label_en}
                            </Text>
                          </View>
                        ))}
                      </>
                    )}
                  </View>

                  {/* Student selector — general (overall) KPI only */}
                  {isGeneralKPI && (
                    <View style={{ marginBottom: 16 }}>
                      <Text style={[styles.fieldLabel, { textAlign: isRTL ? 'right' : 'left', marginBottom: 8 }]}>
                        <Text style={styles.requiredStar}>* </Text>
                        {isRTL ? 'الطالب' : 'Student'}
                      </Text>
                      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, justifyContent: isRTL ? 'flex-end' : 'flex-start' }}>
                        {students.length === 0 ? (
                          <Text style={{ color: '#94A3B8', fontSize: 13 }}>
                            {isRTL ? 'لا يوجد طلاب مسجلون' : 'No enrolled students'}
                          </Text>
                        ) : (
                          students.map((s) => {
                            const active = selectedStudentId === s.id;
                            return (
                              <TouchableOpacity
                                key={s.id}
                                onPress={() => setSelectedStudentId(s.id)}
                                style={{
                                  paddingHorizontal: 14,
                                  paddingVertical: 8,
                                  borderRadius: 20,
                                  borderWidth: 1,
                                  backgroundColor: active ? '#A9211F' : '#F1F5F9',
                                  borderColor: active ? '#A9211F' : '#E2E8F0',
                                }}
                              >
                                <Text style={{ color: active ? '#FFFFFF' : '#0F172A', fontSize: 13 }}>{s.name}</Text>
                              </TouchableOpacity>
                            );
                          })
                        )}
                      </View>
                    </View>
                  )}

                  {/* 2. Value Fields ────────────────────────── */}
                  <View style={[styles.valuesRow, isMobile && styles.valuesRowMobile, !isMobile && { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
                    {/* Achieved value */}
                    <View style={[styles.valueField, isMobile && styles.fullWidth]}>
                      <Text style={[styles.fieldLabel, { textAlign: isRTL ? 'right' : 'left' }]}>
                        <Text style={styles.requiredStar}>* </Text>
                        {isRTL ? 'القيمة المحققة' : 'Achieved Value'}
                      </Text>
                      <View style={[styles.inputWithSuffix, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
                        <TextInput
                          style={[styles.input, styles.inputFlex, { textAlign: isRTL ? 'right' : 'left' }]}
                          value={achievedValue}
                          onChangeText={setAchievedValue}
                          placeholder="0.0"
                          placeholderTextColor="#9CA3AF"
                          keyboardType="numeric"
                        />
                        <View style={styles.inputSuffix}>
                          <Text style={styles.inputSuffixText}>{kpiUnitDisplay}</Text>
                        </View>
                      </View>
                    </View>

                    {/* Target value (read-only) */}
                    <View style={[styles.valueField, isMobile && styles.fullWidth]}>
                      <Text style={[styles.fieldLabel, { textAlign: isRTL ? 'right' : 'left' }]}>
                        {isRTL ? 'القيمة المستهدفة' : 'Target Value'}
                      </Text>
                      <View style={[styles.inputWithSuffix, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
                        <View style={[styles.input, styles.inputFlex, styles.inputReadOnly]}>
                          <Text style={[styles.inputReadOnlyText, { textAlign: isRTL ? 'right' : 'left' }]}>
                            {targetValue}
                          </Text>
                        </View>
                        <View style={styles.inputSuffix}>
                          <Text style={styles.inputSuffixText}>{kpiUnitDisplay}</Text>
                        </View>
                      </View>
                    </View>
                  </View>

                  {/* Info bar */}
                  <View style={[styles.infoBar, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
                    <Ionicons name="information-circle-outline" size={16} color="#64748B" />
                    <Text style={[styles.infoBarText, { textAlign: isRTL ? 'right' : 'left' }]}>
                      {isRTL
                        ? 'سيتم حساب نسبة الإنجاز بناءً على القيمة المدخلة والمستهدف'
                        : 'Achievement percentage will be calculated based on entered and target values'}
                    </Text>
                  </View>

                  {/* 3. Evidence Section ────────────────────── */}
                  <View style={styles.evidenceSection}>
                    <Text style={[styles.fieldLabel, { textAlign: isRTL ? 'right' : 'left', marginBottom: 12 }]}>
                      <Text style={styles.requiredStar}>* </Text>
                      {isRTL ? 'إضافة الدليل' : 'Add Evidence'}
                    </Text>

                    {/* Option 1: File */}
                    <View style={[styles.evidenceOption, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
                      <RadioButton selected={evidenceMode === 'file'} onPress={() => setEvidenceMode('file')} />
                      <Text style={[styles.evidenceOptionLabel, { textAlign: isRTL ? 'right' : 'left' }]}>
                        {isRTL ? 'إرفاق ملف الدليل' : 'Attach evidence file'}
                      </Text>
                    </View>
                    {evidenceMode === 'file' && (
                      <View style={styles.evidenceBody}>
                        <TouchableOpacity style={styles.filePickerBtn} onPress={handleFilePick}>
                          <Ionicons name="cloud-upload-outline" size={16} color={FOREGROUND} />
                          <Text style={styles.filePickerBtnText}>
                            {evidenceFile ? evidenceFile.name : (isRTL ? 'اختر ملف' : 'Choose file')}
                          </Text>
                        </TouchableOpacity>
                        <Text style={[styles.evidenceHint, { textAlign: isRTL ? 'right' : 'left' }]}>
                          {isRTL
                            ? 'يمكنك إرفاق ملفات الصور أو المستندات'
                            : 'You can attach image or document files'}
                        </Text>
                      </View>
                    )}

                    {/* Option 2: Text */}
                    <View style={[styles.evidenceOption, { flexDirection: isRTL ? 'row-reverse' : 'row', marginTop: 12 }]}>
                      <RadioButton selected={evidenceMode === 'text'} onPress={() => setEvidenceMode('text')} />
                      <Text style={[styles.evidenceOptionLabel, { textAlign: isRTL ? 'right' : 'left' }]}>
                        {isRTL ? 'إدخال نص' : 'Enter text'}
                      </Text>
                    </View>
                    {evidenceMode === 'text' && (
                      <View style={styles.evidenceBody}>
                        <TextInput
                          style={[styles.input, styles.textArea, { textAlign: isRTL ? 'right' : 'left' }]}
                          value={evidenceText}
                          onChangeText={setEvidenceText}
                          placeholder={isRTL ? 'اكتب شرح الدليل هنا...' : 'Write evidence description here...'}
                          placeholderTextColor="#9CA3AF"
                          multiline
                          numberOfLines={4}
                        />
                        <Text style={[styles.evidenceHint, { textAlign: isRTL ? 'right' : 'left' }]}>
                          {isRTL
                            ? 'يمكنك كتابة شرح نصي للدليل'
                            : 'You can write a text explanation of the evidence'}
                        </Text>
                      </View>
                    )}
                  </View>

                  {/* 4. Notes ───────────────────────────────── */}
                  <View style={styles.notesSection}>
                    <Text style={[styles.fieldLabel, { textAlign: isRTL ? 'right' : 'left' }]}>
                      {isRTL ? 'ملاحظات (اختياري)' : 'Notes (optional)'}
                    </Text>
                    <TextInput
                      style={[styles.input, styles.textArea, { textAlign: isRTL ? 'right' : 'left' }]}
                      value={notes}
                      onChangeText={setNotes}
                      placeholder={isRTL ? 'أدخل ملاحظاتك هنا...' : 'Enter your notes here...'}
                      placeholderTextColor="#9CA3AF"
                      multiline
                      numberOfLines={3}
                    />
                  </View>

                  {/* 5. Footer buttons ──────────────────────── */}
                  <View style={[styles.footerButtons, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
                    <TouchableOpacity
                      style={[styles.saveBtn, isSaving && styles.saveBtnDisabled]}
                      onPress={handleSave}
                      disabled={isSaving}
                    >
                      {isSaving ? (
                        <ActivityIndicator size="small" color="#FFFFFF" />
                      ) : (
                        <Text style={styles.saveBtnText}>{isRTL ? 'حفظ' : 'Save'}</Text>
                      )}
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={styles.cancelBtn}
                      onPress={() => navigation.goBack()}
                      disabled={isSaving}
                    >
                      <Text style={styles.cancelBtnText}>{isRTL ? 'إلغاء' : 'Cancel'}</Text>
                    </TouchableOpacity>
                  </View>
                    </View>
                  )}
                </View>

              </View>
            </View>
          )}
        </View>
      </ScrollView>
      <Toast
        visible={toastVisible}
        message={toastMessage}
        type={toastType}
        onHide={() => setToastVisible(false)}
      />
    </PageContainer>
  );
}

// ════════════════════════════════════════════════════════════
// Styles
// ════════════════════════════════════════════════════════════
const styles = StyleSheet.create({
  scrollView: { flex: 1 },
  scrollContent: { flexGrow: 1 },
  container: { flex: 1 },
  loadingWrap: { flex: 1, justifyContent: 'center', alignItems: 'center', paddingVertical: 80 },
  sections: { gap: 20 },

  // Card
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    padding: 20,
  },

  // Title card
  kpiNameTitle: {
    fontSize: 20,
    fontFamily: FONTS.bold,
    color: FOREGROUND,
    lineHeight: 28,
  },

  // Tab bar
  tabBar: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    padding: 12,
    alignItems: 'flex-end',
  },
  tabRow: {
    gap: 0,
  },
  tabBtn: {
    paddingHorizontal: 20,
    paddingVertical: 8,
    borderRadius: 6,
  },
  tabBtnActive: {
    backgroundColor: '#A6813F',
  },
  tabBtnText: {
    fontSize: 13,
    fontFamily: FONTS.medium,
    color: '#64748B',
  },
  tabBtnTextActive: {
    color: '#FFFFFF',
  },

  // Import tab
  importSection: {
    backgroundColor: '#F8FAFC',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    padding: 20,
    marginBottom: 20,
  },
  importSectionTitle: {
    fontSize: 15,
    fontFamily: FONTS.medium,
    color: FOREGROUND,
  },
  importFileInput: {
    marginTop: 8,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    backgroundColor: '#FFFFFF',
    overflow: 'hidden',
    alignItems: 'center',
  },
  importChooseBtn: {
    backgroundColor: '#F1F5F9',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderColor: '#E2E8F0',
  },
  importChooseBtnText: {
    fontSize: 13,
    fontFamily: FONTS.medium,
    color: FOREGROUND,
  },
  importFileNameBox: {
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  importFileNameText: {
    fontSize: 13,
    fontFamily: FONTS.regular,
    color: '#94A3B8',
  },
  checkboxRow: {
    alignItems: 'center',
    gap: 10,
    marginTop: 16,
  },
  checkbox: {
    width: 20,
    height: 20,
    borderRadius: 4,
    borderWidth: 1.5,
    borderColor: '#CBD5E1',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FFFFFF',
  },
  checkboxChecked: {
    backgroundColor: '#414042',
    borderColor: '#414042',
  },
  checkboxLabel: {
    fontSize: 13,
    fontFamily: FONTS.regular,
    color: FOREGROUND,
    flex: 1,
  },
  importInstructions: {
    backgroundColor: '#F8FAFC',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    padding: 20,
    marginBottom: 20,
    gap: 8,
  },
  importInstructionsTitle: {
    fontSize: 14,
    fontFamily: FONTS.bold,
    color: FOREGROUND,
    marginBottom: 4,
  },
  instructionRow: {
    gap: 8,
    alignItems: 'flex-start',
  },
  instructionBullet: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#64748B',
    lineHeight: 20,
  },
  instructionText: {
    fontSize: 13,
    fontFamily: FONTS.regular,
    color: '#64748B',
    lineHeight: 20,
    flex: 1,
  },

  // Grid
  gridRow: { flexDirection: 'row-reverse' as const, gap: 20 },
  gridRowMobile: { flexDirection: 'column' },
  fullWidth: { flex: undefined, width: '100%' as any },
  formCard: { flex: 2 },
  sidebarCol: { flex: 1, gap: 20 },

  // Section title
  sectionTitle: {
    fontSize: 15,
    fontFamily: FONTS.bold,
    color: FOREGROUND,
    marginBottom: 16,
    lineHeight: 22,
  },

  // ── Period section ──────────────────────────────────────
  periodSection: {
    backgroundColor: '#F1F5F9',
    borderRadius: 8,
    padding: 16,
    marginBottom: 20,
  },
  periodGroupLabel: {
    fontSize: 13,
    fontFamily: FONTS.medium,
    color: '#334155',
    marginBottom: 10,
  },
  periodGroupLabelOverdue: {
    marginTop: 14,
  },
  periodRow: {
    alignItems: 'center',
    gap: 10,
    marginBottom: 8,
  },
  periodText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: FOREGROUND,
  },
  periodTextOverdue: {
    color: '#d62728',
  },

  // ── Radio button ────────────────────────────────────────
  radio: {
    width: 16,
    height: 16,
    borderRadius: 8,
    borderWidth: 1.5,
    borderColor: '#E2E8F0',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FFFFFF',
  },
  radioSelected: {
    borderColor: '#A9211F',
  },
  radioDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#A9211F',
  },

  // ── Value fields ────────────────────────────────────────
  valuesRow: { flexDirection: 'row' as const, gap: 16, marginBottom: 12 },
  valuesRowMobile: { flexDirection: 'column' },
  valueField: { flex: 1 },
  fieldLabel: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#334155',
    marginBottom: 8,
  },
  requiredStar: {
    color: '#EF4444',
    fontSize: 14,
  },
  inputWithSuffix: {
    flexDirection: 'row' as const,
    alignItems: 'stretch',
  },
  input: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    minHeight: 36,
    paddingHorizontal: 12,
    paddingVertical: 8,
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: FOREGROUND,
  },
  inputFlex: { flex: 1 },
  inputReadOnly: {
    backgroundColor: '#F1F5F9',
    justifyContent: 'center',
  },
  inputReadOnlyText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#64748B',
  },
  inputSuffix: {
    backgroundColor: '#F1F5F9',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    minHeight: 36,
    paddingHorizontal: 10,
    justifyContent: 'center',
    alignItems: 'center',
    marginStart: -1,
  },
  inputSuffixText: {
    fontSize: 13,
    fontFamily: FONTS.regular,
    color: '#64748B',
  },
  textArea: {
    minHeight: 80,
    textAlignVertical: 'top',
  },

  // ── Info bar ────────────────────────────────────────────
  infoBar: {
    backgroundColor: '#F1F5F9',
    borderRadius: 6,
    paddingHorizontal: 12,
    paddingVertical: 10,
    alignItems: 'center',
    gap: 8,
    marginBottom: 20,
  },
  infoBarText: {
    flex: 1,
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#64748B',
    lineHeight: 18,
  },

  // ── Evidence section ────────────────────────────────────
  evidenceSection: {
    backgroundColor: '#F1F5F9',
    borderRadius: 8,
    padding: 16,
    marginBottom: 20,
  },
  evidenceOption: {
    alignItems: 'center',
    gap: 10,
  },
  evidenceOptionLabel: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: FOREGROUND,
  },
  evidenceBody: {
    marginTop: 10,
    paddingStart: 26,
  },
  filePickerBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 14,
    paddingVertical: 8,
    alignSelf: 'flex-start',
  },
  filePickerBtnText: {
    fontSize: 13,
    fontFamily: FONTS.regular,
    color: FOREGROUND,
  },
  evidenceHint: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#94A3B8',
    marginTop: 8,
  },

  // ── Notes ───────────────────────────────────────────────
  notesSection: {
    marginBottom: 20,
  },

  // ── Footer buttons ──────────────────────────────────────
  footerButtons: {
    gap: 12,
    alignItems: 'center',
  },
  saveBtn: {
    backgroundColor: '#A9211F',
    borderRadius: 8,
    minHeight: 36,
    paddingHorizontal: 16,
    paddingVertical: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  saveBtnDisabled: {
    backgroundColor: '#D1D5DB',
  },
  saveBtnText: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#FFFFFF',
  },
  cancelBtn: {
    borderWidth: 1,
    borderColor: '#CBD5E1',
    borderRadius: 8,
    minHeight: 36,
    paddingHorizontal: 16,
    paddingVertical: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  cancelBtnText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: FOREGROUND,
  },

  // ── Sidebar: Info rows ──────────────────────────────────
  infoRow: {
    alignItems: 'center',
    gap: 10,
    marginBottom: 10,
  },
  infoLabelPill: {
    backgroundColor: 'rgba(51,65,85,0.1)',
    borderRadius: 4,
    paddingHorizontal: 4,
    paddingVertical: 2,
    width: 80,
    justifyContent: 'center',
  },
  infoLabelText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#334155',
  },
  infoValueText: {
    flex: 1,
    fontSize: 13,
    fontFamily: FONTS.regular,
    color: FOREGROUND,
  },

  // ── Sidebar: Achievement ────────────────────────────────
  achievementRow: {
    alignItems: 'center',
    gap: 16,
  },
  achievementMetrics: {
    flex: 1,
    gap: 10,
  },
  achievementMetricRow: {
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  achievementMetricLabel: {
    fontSize: 13,
    fontFamily: FONTS.regular,
    color: '#64748B',
  },
  achievementMetricValue: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: FOREGROUND,
  },

  // ── Sidebar: Recent readings ────────────────────────────
  recentReadingRow: {
    backgroundColor: '#F1F5F9',
    borderRadius: 6,
    paddingHorizontal: 12,
    paddingVertical: 10,
    marginBottom: 8,
  },
  recentReadingValue: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: FOREGROUND,
    marginBottom: 2,
  },
  recentReadingDate: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#94A3B8',
  },
});
